import { useClaims } from '@backoffice-frontend/authentication'
import { formatDateTime, getLanguage } from '@backoffice-frontend/common'
import { useErrorHandlingQuery } from '@backoffice-frontend/data-fetching'
import { Restricted } from '@backoffice-frontend/restricted'
import { useGetServiceAreasQuery } from '@backoffice-frontend/service-area-picker'
import { useBackofficeLoggedInUser } from '@backoffice-frontend/user-management-ui'
import { BoumRight } from '@moia-dev/moia-token-claims'
import { Accordion, LoadingIndicator } from '@moia-dev/pace-web'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AccountLayout } from './components/AccountLayout'
import { TagGroup } from './components/TagGroup'
import { usePermissionsViewQuery } from './PermissionsView.hook'

export const PermissionsView = () => {
  const { data: serviceAreasData } = useGetServiceAreasQuery()
  const { t, i18n } = useTranslation()

  const { user, loading: groupsLoading } = useBackofficeLoggedInUser()
  const claims = useClaims()

  const { data, loading: queryLoading } = useErrorHandlingQuery(
    usePermissionsViewQuery,
    { variables: { email: claims.cognito.email() } },
  )

  const filteredServiceAreas = serviceAreasData?.serviceAreas.filter(it =>
    claims.boum.serviceAreas().includes(it.uuid),
  )

  const [rights, setRights] = useState<string[]>([])
  useEffect(() => {
    setRights(claims.boum.userRights().toJSON().sort())
  }, [claims])

  const groupsApprover = user?.groupsApprover
  const { customerTenants, operatorTenants, ridepoolingServiceTenants } =
    claims.boum

  return (
    <AccountLayout
      header={t('authenticatedAppView_permissions_header')}
      subheader={t('authenticatedAppView_permissions_caption')}
    >
      <div>
        <Accordion
          title={t('authenticatedAppView_yourOperatorTenants')}
          compact
          disabled={operatorTenants().length === 0}
        >
          <TagGroup
            items={operatorTenants()}
            getKey={tenant => tenant}
            getLabel={tenant => tenant}
          />
        </Accordion>

        <Accordion
          title={t('authenticatedAppView_yourCustomerTenants')}
          compact
          disabled={customerTenants().length === 0}
        >
          <TagGroup
            items={customerTenants()}
            getKey={tenant => tenant}
            getLabel={tenant => tenant}
          />
        </Accordion>

        <Accordion
          title={t('authenticatedAppView_yourRidepoolingServiceTenants')}
          compact
          disabled={ridepoolingServiceTenants().length === 0}
        >
          <TagGroup
            items={ridepoolingServiceTenants()}
            getKey={tenant => tenant}
            getLabel={tenant => tenant}
          />
        </Accordion>

        {!queryLoading && (
          <Accordion
            title={t('authenticatedAppView_yourFleets')}
            compact
            disabled={data?.fleets?.length === 0}
          >
            <TagGroup
              items={data?.fleets}
              getKey={fleet => fleet.id}
              getLabel={fleet => fleet.name}
            />
          </Accordion>
        )}

        <Accordion
          title={t('authenticatedAppView_yourServiceAreas')}
          compact
          disabled={filteredServiceAreas?.length === 0}
        >
          <TagGroup
            items={filteredServiceAreas}
            getKey={serviceArea => serviceArea.uuid}
            getLabel={serviceArea =>
              serviceArea.displayNames[getLanguage(i18n.language)] ??
              serviceArea.id
            }
          />
        </Accordion>

        <Accordion
          title={t('authenticatedAppView_yourGroups')}
          compact
          disabled={user?.timedGroups.length === 0}
        >
          {groupsLoading ? (
            <LoadingIndicator />
          ) : (
            <TagGroup
              items={user?.timedGroups}
              getKey={group => group.groupName}
              getLabel={group =>
                `${group.displayName ?? group.groupName}${
                  group.expires
                    ? ` (${t('common_expires_label')} ${formatDateTime(group.expires)})`
                    : ''
                }`
              }
            />
          )}
        </Accordion>

        <Accordion
          title={t('authenticatedAppView_yourRights')}
          compact
          disabled={rights.length === 0}
        >
          <TagGroup
            items={rights}
            getKey={right => right}
            getLabel={right => right}
          />
        </Accordion>

        <Restricted requiredRights={[BoumRight.BACKOFFICE_APPROVER_FEATURE]}>
          <Accordion
            title={t('authenticatedAppView_groupsYouApprove')}
            compact
            disabled={groupsApprover?.length === 0}
          >
            <TagGroup
              items={groupsApprover}
              getKey={group => group}
              getLabel={(groupName: string) =>
                data?.groups?.find(group => group.name === groupName)
                  ?.displayName ?? groupName
              }
            />
          </Accordion>
        </Restricted>
      </div>
    </AccountLayout>
  )
}
